import type { Event } from '@sentry/react';

/**
 * Sanitize every carrier of the invitation-link secret (and, generically,
 * any URL/query) out of browser Sentry events before they leave the
 * process.
 *
 * Verified against the installed SDK (`@sentry/core@10.65.0`,
 * `@sentry/browser@10.65.0`, `@sentry/replay@10.65.0`) by reading
 * `node_modules`, not documentation — see
 * `docs/audit/2026-07-31-egress-ui-worker-sentry.md` for every citation this
 * module closes. Modeled on the server-side sibling,
 * `apps/edge-api/src/utils/sentry-event-mask.ts` (read for the pattern, not
 * imported — `apps/edge-api` is a different remediation workstream and this
 * repo's browser and Cloudflare-Workers Sentry SDKs populate different
 * fields under different names; see the per-field notes below).
 *
 * ## Why one function reaches error events, transaction events, AND replay
 *
 * `beforeSend` only runs for error events and `beforeSendTransaction` only
 * for transaction events (`@sentry/core/build/cjs/client.js:734-739,
 * 785-793, 797-799` — gated on `event.type === undefined` vs the
 * `beforeSendTransaction` branch). Session Replay is worse: it bypasses
 * `client.captureEvent`/`processBeforeSend` entirely — `sendReplayRequest`
 * calls `core.prepareEvent(...)` and `transport.send(...)` directly
 * (`@sentry/replay/build/npm/cjs/index.js:7007-7022, 7040-7083`), so
 * **neither** `beforeSend` nor `beforeSendTransaction` ever sees a replay
 * event.
 *
 * What all three DO share is `prepareEvent()`
 * (`@sentry/core/build/cjs/utils/prepareEvent.js`): the normal capture path
 * calls it via `client._prepareEvent` BEFORE `processBeforeSend`
 * (`client.js:596` → `:605`), and Replay's `sendReplayRequest` calls the
 * exact same exported function directly. `prepareEvent()` runs every
 * registered event processor — `client.getEventProcessors()` plus the
 * isolation/current scope's own (`prepareEvent.js:34,41-45`) — over
 * `prepared` before returning. A processor registered globally via
 * `Sentry.addEventProcessor()` (which attaches to the isolation scope,
 * `@sentry/core/build/cjs/exports.js:122-123`) therefore runs for error
 * events, transaction events, AND the replay envelope event alike, with no
 * separate wiring needed. This is proven, not assumed — see
 * `sentry-event-processor-architecture.test.ts`, which drives the real
 * installed SDK (a `BrowserClient` with a fake transport) through
 * `prepareEvent()` exactly the way Replay does and asserts the processor ran.
 *
 * This is why `sentry.ts` wires this function in via
 * `Sentry.addEventProcessor()` rather than `beforeSend`/
 * `beforeSendTransaction` — two separate call sites for the same masking
 * logic is exactly the "asymmetry between channels" failure pattern this
 * remediation program calls out (`docs/plans/2026-07-31-egress-remediation.md`
 * §"Асимметрия каналов"): one gets edited, the other silently doesn't.
 *
 * ## What this function does NOT reach
 *
 * The replay envelope event (`urls[]`, handled below) is the only replay
 * field reachable this way. The rrweb *recording* itself (DOM snapshots,
 * the `Meta` event's `data.href`, individual navigation/network "custom"
 * events) is a separate payload that never passes through `prepareEvent()`
 * — only `beforeAddRecordingEvent` (wired separately in `sentry.ts`) can
 * touch it, and only for `EventType.Custom` entries; the periodic `Meta`
 * checkpoint event carrying `window.location.href` verbatim
 * (`@sentry/replay/build/npm/cjs/index.js:4290-4297`) is reachable by NO
 * documented hook in this SDK version. See `sentry.ts` and the remediation
 * report for what that leaves open.
 *
 * ## Per-field notes (browser SDK, not the Cloudflare one)
 *
 * - `event.request.url` / `url.full` / `http.url` / bare `url` — all four
 *   attribute names exist in the installed browser SDK (unlike the
 *   Cloudflare SDK's single `url.full`): the pageload/navigation root span
 *   attributes use `url.path`/`url.full`
 *   (`@sentry/browser/.../tracing/browserTracingIntegration.js:90-95`);
 *   `fetch`/`xhr` child spans additionally get `http.url` and a bare `url`
 *   (`@sentry/core/build/cjs/fetch.js:190-204`,
 *   `@sentry/browser/.../tracing/request.js:37-46,160-181`). Missing any one
 *   of the four leaves the same secret reachable through a sibling field.
 * - `http.query` — the browser SDK's own query-string attribute name on
 *   `fetch`/`xhr` spans (`fetch.js:203`, `tracing/request.js:180`); NOT
 *   `url.query` (that name belongs to the Cloudflare/server SDK's
 *   `getHttpSpanDetailsFromUrlObject`, confirmed absent from every browser
 *   tracing file grepped for this module). Dropped outright, matching the
 *   server sibling's reasoning: a query string is a general risk, not just
 *   a place this one token happens to live.
 * - `event.request.query_string` / `event.request.data` — the Cloudflare
 *   SDK's `requestDataIntegration`/`httpServerIntegration` populate these;
 *   neither integration is in the browser SDK's default set
 *   (`@sentry/browser/.../sdk.js` default-integrations list has no
 *   `requestDataIntegration`), and nothing in this project adds them. Not
 *   handled here because no carrier was found — recorded as unconfirmed,
 *   not silently assumed absent (see the remediation report).
 * - `event.request.headers` — populated two ways in this SDK: `HttpContext`
 *   (disabled entirely in `sentry.ts`, see the comment there) for error
 *   events, and `browserTracingIntegration`'s own
 *   `scope.setSDKProcessingMetadata({ normalizedRequest: ... })`
 *   (`browserTracingIntegration.js:242-244,349-355`) for transaction events
 *   — the latter is NOT gated by `HttpContext` and still fires. Both put
 *   `Referer`/`User-Agent` under `headers` via `getHttpRequestData()`
 *   (`@sentry/browser/.../helpers.js:88-101`); `Referer` is `document.
 *   referrer` and can carry the invitation token if the user reloads off
 *   the invitation page (audit §A4). ONLY `Referer` is stripped — `User-
 *   Agent` is not a URL/secret carrier and is the field Sentry uses to
 *   identify browser/OS for triage; dropping the whole `headers` object (an
 *   earlier version of this function did) silently disabled that for every
 *   transaction event. Review round 1, I4:
 *   `docs/audit/2026-07-31-remediation-ws3-ui-sentry-review.md`.
 * - `event.spans[].description` — the browser SDK, unlike the Cloudflare
 *   one, puts the human-readable span NAME (`${method} ${sanitizedUrl}` for
 *   `http.client` spans, e.g. `GET /api/v1/invitations/<token>`) on
 *   `SpanJSON.description`, confirmed via `spanToJSON()`
 *   (`@sentry/core/build/cjs/utils/spanUtils.js:96-107`:
 *   `description: name`). The parent audit's §10 table assumed this needed
 *   the more granular `beforeSendSpan` hook — it doesn't: `description` is
 *   already present on `event.spans[]` by the time a global event processor
 *   or `beforeSendTransaction` runs, so the same rewrite used for
 *   `event.transaction` closes it here too — but see `maskMethodAndPath`
 *   below: an earlier version of that rewrite masked only the token SHAPE
 *   in span descriptions and left the surrounding query string (e.g.
 *   `APP_SID`) intact, because it never called `maskUrlLike`. Review round
 *   1, I1, with a measured carrier: navigation-timing child spans built by
 *   `@sentry/browser-utils/build/cjs/metrics/browserMetrics.js`'s
 *   `_addNavigationSpans`/`_addRequest` set `description` to
 *   `PerformanceNavigationTiming.name` — the full page URL with query
 *   string, a snapshot at document-fetch time that a later
 *   `history.replaceState` does NOT update (so query params this app
 *   deliberately scrubs from the address bar, e.g. `sessionId`/`bootstrap`
 *   in `embedded-session.ts`, still reach this field).
 * - `event.breadcrumbs` — `category: 'navigation'` breadcrumbs carry
 *   `data.from`/`data.to` as RELATIVE paths (`path+query+hash`, no origin —
 *   `@sentry/core/build/cjs/utils/url.js:103-111`, `relative: match[5] +
 *   query + fragment`), and `category: 'fetch'|'xhr'` breadcrumbs carry
 *   `data.url` as an absolute or relative URL depending on the request. A
 *   masker that only accepts absolute URLs (`new URL(...)`) silently no-ops
 *   on the relative shape — the same class of bug as the freeze-monitor
 *   regex this program is also fixing (a matcher anchored to one shape that
 *   misses a sibling shape). `maskUrlLike` below handles both.
 * - `event.breadcrumbs`, `category: 'console'` — the live source in this
 *   app's config is `breadcrumbsIntegration()`'s `console: true` suboption
 *   (`@sentry/browser/.../integrations/breadcrumbs.js`,
 *   `_getConsoleBreadcrumbHandler`), on by default and never disabled in
 *   `sentry.ts`'s `buildSentryOptions` — NOT the separate opt-in
 *   `consoleIntegration` (`@sentry/core/integrations/console.js`, absent
 *   from `getDefaultIntegrations()` and never added here); both build the
 *   same breadcrumb shape, only the first is actually wired up. Two
 *   carriers, driven against the real SDK (not assumed) in
 *   `sentry-console-breadcrumb-architecture.test.ts`:
 *   `data.arguments` is the RAW `console.<level>(...)` argument array —
 *   objects and Errors ride through whole (`logger.ts`'s `context`
 *   parameter, or any `console.error(label, err)` call site across this
 *   app, several of them ungated by `import.meta.env.DEV`); `message` is
 *   the SDK's own formatted join of those arguments (`safeJoin` /
 *   `formatConsoleArgs`) — a primitive string argument, or an `Error`'s
 *   `.message`, lands in it verbatim, but a plain object does NOT (its
 *   `stringifyValue` falls through to the generic `[object Object]`
 *   placeholder — measured, not assumed; see the architecture test). Both
 *   fields need masking; `data.arguments` needs it more, since it is the
 *   only one that can carry a whole object.
 *
 * ## Why this masks console/ui.click content instead of deleting it — unlike
 * the server-side sibling
 *
 * `apps/edge-api/src/utils/sentry-event-mask.ts` closes the SAME class of
 * leak (console-derived breadcrumb content) by deleting `data`/`message`
 * outright, not masking them. This file does the opposite — mask by known
 * shape, keep the breadcrumb — and both are correct for where they run, for
 * three separable reasons (see that file's own docblock for its half of
 * this cross-reference):
 *
 * 1. **What else records the event.** On the server, the identical content
 *    already lives sanitized in structured logs AND in the (also sanitized)
 *    console output that produced the breadcrumb in the first place — the
 *    breadcrumb is a third copy, and deleting it loses nothing already
 *    recorded elsewhere. The browser has no structured log at all: a
 *    console breadcrumb is the ONLY trace of "what happened right before
 *    this error" that ever leaves the tab. Deleting it here would delete
 *    the only copy, not a redundant one.
 * 2. **Whether an allowlist is possible.** The server's `context` parameter
 *    is an arbitrary object passed at roughly 350 call sites across that
 *    codebase — enumerating safe keys there would be an allowlist with
 *    effectively zero entries, i.e. no allowlist at all. This file's mask
 *    (`maskInvitationTokenInPath`, `stripSelectorAttributeValues`) instead
 *    knows the SHAPE of what it's removing (a path segment, a bracketed
 *    attribute value) — narrow enough to write and verify, which the
 *    server's `context` shape is not.
 * 3. **What survives a change in the carrier's form.** Deletion survives a
 *    field being renamed or its content changing shape; masking by known
 *    shape does not — a masker only removes what it recognizes. This
 *    already happened once: a sibling workstream changed a shared logger's
 *    argument from an object to a string, and the server-side deletion
 *    fix needed no changes to keep working. The masking approach here would
 *    NOT have survived that same change unscathed — a different string
 *    shape can slip past a mask written for the old one. That fragility is
 *    the price this file pays, deliberately, to keep the diagnostic value
 *    reason 1 above describes; it is not an oversight.
 */

// Matches the ROUTE, not the SECRET'S SHAPE. First cut of this function
// matched a UUID chain instead (a denylist of the token's current form) —
// exactly the anti-pattern the parent program calls out for path masking in
// general (`docs/plans/2026-07-31-egress-remediation.md` §1: mask what
// isn't recognized as safe, not what's recognized as a known secret shape),
// and the same class of bug `freeze-monitor.ts`'s ID regex had (anchored to
// one shape, silently misses a sibling one). If the token generator ever
// changes shape, this form keeps masking; a shape-based one would silently
// stop. Mirrors the server-side sibling's approach
// (`apps/edge-api/src/utils/security.ts:112`,
// `maskInvitationTokenInPath`) — not imported (different module, different
// SDK's URL shapes — see this file's docblock), but the SAME principle: SPA
// has exactly one route under `/invitations/` (`/invitations/$token` —
// checked against all 56 `createFileRoute` calls in
// `apps/ui-worker/src/routes/`), so matching the route there is safe.
//
// The SERVER side is NOT that simple — review round 2, I5:
// `docs/audit/2026-07-31-remediation-ws3-ui-sentry-review.md`. There are
// FIVE routes under `/api/v1/invitations/`
// (`apps/edge-api/src/routes/invitations.ts:215,281,343,421,517` —
// `/list`, `/revoke/:id`, `/resend/:id`, `/:token`, `/:token/accept`), all
// called from this app (`apps/ui-worker/src/lib/api/domains/
// invitations.api.ts:51-64`). An earlier version of this pattern had no
// exclusion for the three literal siblings and over-matched them —
// `/api/v1/invitations/list` became `/api/v1/invitations/:token`, and
// `/api/v1/invitations/revoke/<id>` became `/api/v1/invitations/:token/<id>`
// (masking the ACTION NAME and leaving the id raw — backwards from what's
// useful, though not a leak: the id isn't a secret). The negative lookahead
// below excludes exactly those three literals, mirroring why the server
// sibling's own pattern is anchored (`security.ts:99-103` explains it
// there). Failure direction is safe either way: a route this exclusion list
// doesn't know about yet just gets over-masked, same as before this fix —
// never under-masked.
//
// Deliberately NOT anchored to the whole string: this function runs against
// full absolute URLs, bare paths, and breadcrumb "relative" strings alike.
const INVITATION_TOKEN_IN_PATH =
  /(\/(?:api\/v1\/)?invitations)\/(?!list(?:[/?#]|$)|revoke\/|resend\/)[^/?#]+(\/accept)?/gi;

/**
 * Mask the invitation-token path segment wherever it occurs in a string —
 * not anchored, so it works equally on a bare pathname, a full URL, or a
 * breadcrumb's relative `path+query+hash` string.
 */
export function maskInvitationTokenInPath(value: string): string {
  return value.replace(
    INVITATION_TOKEN_IN_PATH,
    (_match, prefix: string, accept: string | undefined) =>
      `${prefix}/:token${accept ?? ''}`
  );
}

// Matches a `[attr="value"]` segment of `htmlTreeAsString()`'s selector
// output (`@sentry/browser-utils/build/cjs/htmlTreeAsString.js:83-107`:
// `[${keyAttrPair[0]}="${keyAttrPair[1]}"]` for the fixed allowlist
// `["aria-label", "type", "name", "title", "alt"]`, and the same bracket
// shape for a custom `keyAttrs` option — this app doesn't set one, but the
// pattern is generic either way). Does NOT match `#id` or `.class` — those
// are bare tokens in the selector, not bracketed attribute pairs, and
// review round 1 raised no carrier through either of them.
//
// The VALUE half is `[\s\S]*?` (lazy, any character) with a lookahead for
// the real closing `"]`, not `[^"]*` (review round 2, Important — a live
// leak on `main`, not a hypothetical): `htmlTreeAsString` writes the
// attribute value verbatim, unescaped
// (`` `[${k}="${attr}"]` ``, `htmlTreeAsString.js:101-104` — no quote
// escaping anywhere in this function). A value containing a literal `"` —
// achievable today, e.g. a call annotation quoting what someone said
// (`TranscriptViewer.tsx:123`, free-text `note`) — breaks a
// `[^"]*`-based pattern: it stops at the FIRST embedded quote, so the
// match ends early and everything from there on (including the real
// closing `"]`) is left completely raw. Reproduced against the real
// installed `htmlTreeAsString`: `title` containing `he said "call
// +380671234567"` came out of the SDK function untouched by the
// `[^"]*` version. The lazy `[\s\S]*?"\]` scans past embedded quotes and
// only stops at a `"]` that is actually followed by a boundary — end of
// string, the start of the ` > ` ancestor separator, or the next `[` of a
// sibling attribute pair (the three shapes `out.join('')`/`.join(' >
// ')` in `htmlTreeAsString.js` can produce after a closing bracket; `.`/
// `#` added defensively — see `_htmlElementAsString`, `id`/`className`
// only ever precede the attribute loop, never follow it, but a shape this
// function hasn't measured must not silently fall back to under-matching
// either). Verified against every case this function is tested against,
// including nested/adjacent embedded quotes, byte-for-byte identical to
// the simpler pattern on every value WITHOUT an embedded quote.
const SELECTOR_ATTRIBUTE_VALUE = /\[([\w-]+)="[\s\S]*?"\](?=$|[ .#[>])/g;

/**
 * Strips the VALUE half of every `[attr="value"]` pair in a `ui.click`/
 * `ui.keypress` breadcrumb's DOM-selector string, keeping the attribute
 * NAME — `span.truncate[title="+380671234567"]` becomes
 * `span.truncate[title]`, including when the value itself contains a
 * literal, unescaped `"` (see `SELECTOR_ATTRIBUTE_VALUE`'s comment).
 * Unlike `maskInvitationTokenInPath`, this isn't matching a known secret's
 * shape: `title`/`aria-label` here carry a customer's phone number, a
 * reviewer's name, a file name — arbitrary business data with no common
 * pattern to detect, so the only closed option is to remove the value
 * outright rather than try to recognize which values are safe. The
 * selector stays useful for debugging (which element, which attribute was
 * set) without carrying whose data it was.
 */
export function stripSelectorAttributeValues(value: string): string {
  return value.replace(SELECTOR_ATTRIBUTE_VALUE, '[$1]');
}

/**
 * A URL-shaped string that may be absolute (`https://host/path?query`) or
 * relative (`/path?query#hash`, Sentry's own breadcrumb `relative` shape).
 * Masks the invitation token in the path and drops the query string
 * entirely (general risk beyond this one known secret — same reasoning as
 * the server-side sibling). Never throws: an unparseable value is returned
 * with the token masked on the raw string rather than left untouched,
 * because falling back to "leave it alone" on a shape this function didn't
 * anticipate is exactly how a secret slips through a masker (see the
 * `Referer`/relative-breadcrumb case this generalizes over).
 */
export function maskUrlLike(value: string): string {
  try {
    const url = new URL(value);
    url.pathname = maskInvitationTokenInPath(url.pathname);
    url.search = '';
    return url.toString();
  } catch {
    // Not an absolute URL — split by hand: path[?query][#hash].
    const hashIdx = value.indexOf('#');
    const beforeHash = hashIdx === -1 ? value : value.slice(0, hashIdx);
    const hash = hashIdx === -1 ? '' : value.slice(hashIdx);
    const queryIdx = beforeHash.indexOf('?');
    const path = queryIdx === -1 ? beforeHash : beforeHash.slice(0, queryIdx);
    return maskInvitationTokenInPath(path) + hash;
  }
}

/**
 * `${method} ${route}` (Sentry span/transaction-name convention) — mask the
 * route half; a bare pathname (no method prefix, e.g. a pageload
 * transaction name) is masked directly.
 *
 * The route half goes through `maskUrlLike` (masks the path AND drops the
 * query string) when it LOOKS like a URL — starts with `/` or a
 * `scheme://` — and through the narrower `maskInvitationTokenInPath`
 * otherwise. This distinction is load-bearing, not cosmetic:
 * `event.spans[].description` is not always an HTTP span name.
 * Navigation-timing child spans (`browser.request`, `browser.response`,
 * `browser.DNS`, … — built by `@sentry/browser-utils/build/cjs/metrics/
 * browserMetrics.js`'s `_addNavigationSpans`/`_addRequest` from
 * `PerformanceNavigationTiming`) get `description` set to the FULL page URL
 * *including query string* — measured leaking `APP_SID` before this fix
 * (review round 1, I1: `docs/audit/2026-07-31-remediation-ws3-ui-sentry-review.md`).
 * But other spans (`measure`, `mark`, custom instrumentation) can carry an
 * arbitrary label as `description`/`transaction` that is NOT a URL and must
 * not be truncated at a `?` that happens to appear in it — hence the guard
 * rather than unconditionally calling `maskUrlLike`.
 *
 * The guard also catches a HOST-RELATIVE form (`host/path?query` — no
 * `scheme://`, no leading `/`) that neither the leading-slash nor the
 * scheme check matches on its own (review round 2, Minor 10). No emitter in
 * the current configuration produces this shape — but "unreachable today"
 * has been wrong before in this same file (round 1 found three separate
 * carriers a prior version assumed didn't exist), so it's closed
 * defensively rather than left as a documented gap. Detection: a segment,
 * then a literal `/`, then a `?` later in the string — `checkout?step=2`
 * (a real non-URL label with a `?` but no `/` before it) does NOT match and
 * stays in the narrow branch, unchanged from before this fix.
 */
function maskMethodAndPath(name: string): string {
  const spaceIdx = name.indexOf(' ');
  const prefix = spaceIdx === -1 ? '' : name.slice(0, spaceIdx + 1);
  const route = spaceIdx === -1 ? name : name.slice(spaceIdx + 1);
  const looksLikeUrl =
    /^(?:\/|[a-z][a-z0-9+.-]*:\/\/)/i.test(route) || /^[^/?#]+\/[^?#]*\?/.test(route);
  return prefix + (looksLikeUrl ? maskUrlLike(route) : maskInvitationTokenInPath(route));
}

// Every attribute name the installed browser SDK uses for a URL string —
// see the docblock above for where each one is set. `http.query` isn't a
// URL string; it's deleted separately below.
const URL_STRING_SPAN_KEYS = ['url', 'url.path', 'url.full', 'http.url'] as const;

function maskSpanAttributeData(data: Record<string, unknown>): void {
  for (const key of URL_STRING_SPAN_KEYS) {
    const value = data[key];
    if (typeof value === 'string') {
      data[key] = maskUrlLike(value);
    }
  }
  delete data['http.query'];
}

function maskBreadcrumbs(event: Event): void {
  if (!event.breadcrumbs) return;
  for (const breadcrumb of event.breadcrumbs) {
    if (breadcrumb.category === 'console' && typeof breadcrumb.message === 'string') {
      // Masked here, ahead of the `!data` guard below, because `message` is
      // a SIBLING of `data`, not nested inside it. Console breadcrumbs are
      // the one category where the SDK also flattens argument content into
      // `message` — see this module's docblock for what actually lands
      // here (a primitive string argument, or an Error's own `.message`).
      breadcrumb.message = maskInvitationTokenInPath(breadcrumb.message);
    } else if (
      (breadcrumb.category === 'ui.click' || breadcrumb.category === 'ui.keypress') &&
      typeof breadcrumb.message === 'string'
    ) {
      // `message` (and, defensively, `data.target` below) is
      // `htmlTreeAsString(clickedElement)` — review round 1, Important: the
      // SDK's own attribute allowlist for this string
      // (`@sentry/browser-utils/build/cjs/htmlTreeAsString.js:62-102`,
      // `["aria-label", "type", "name", "title", "alt"]`, unconditional, no
      // integration option to narrow it) includes `title` and `aria-label` —
      // and this app puts customer data there: a participant's phone number
      // (`components/records/RecordListItem.tsx:160`,
      // `title={participantPhone || undefined}` on the row's clickable
      // button subtree), a reviewer's name (`RecordDetailInline.tsx:292`), a
      // call annotation (`TranscriptViewer.tsx:123`), an uploaded file name
      // (`UploadRecordModal.tsx:495`), a record/project title
      // (`navigation/breadcrumb.tsx:171,191,209`). Measured against the real
      // `htmlTreeAsString` in jsdom on the phone case, not assumed. Stripped
      // by VALUE, not by masking a token shape — none of these carry the
      // invitation token, so `maskInvitationTokenInPath` would silently
      // no-op on all of them; only the generic `[attr="value"]` → `[attr]`
      // rewrite removes them while keeping the selector diagnostically
      // useful (which element, which attribute — just not whose data).
      breadcrumb.message = stripSelectorAttributeValues(breadcrumb.message);
    }

    const data = breadcrumb.data as Record<string, unknown> | undefined;
    // `typeof data !== 'object'` guards a PRIMITIVE `data` (e.g. a string) —
    // review round 1, Minor: `'arguments' in data` below throws on anything
    // that isn't an object, and a thrown event processor drops the WHOLE
    // event (`prepareEvent`'s `notifyEventProcessors` has no partial-failure
    // mode). The installed SDK never builds `data` as a non-object, but this
    // function's own stated principle — an unrecognized shape must not be
    // let through, here read as "must not be let through as a crash either"
    // — applies to `data`'s own shape, not just `data.arguments`'s.
    if (!data || typeof data !== 'object') continue;

    if (breadcrumb.category === 'navigation') {
      if (typeof data.from === 'string') data.from = maskUrlLike(data.from);
      if (typeof data.to === 'string') data.to = maskUrlLike(data.to);
    } else if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
      if (typeof data.url === 'string') data.url = maskUrlLike(data.url);
    } else if (breadcrumb.category === 'console') {
      // `data.arguments` is the RAW args array the call site passed to
      // `console.<level>(...)` — the severe carrier, since an object or an
      // Error rides through whole (unlike `message` above, which only ever
      // sees a flattened string). Reuses `maskStringsDeep` — built for this
      // exact array shape already (`maskReplayRecordingEvent`'s console
      // breadcrumb handling, below), including its array-recursion mutation
      // guard (review round 1, Minor 1).
      if (Array.isArray(data.arguments)) {
        maskStringsDeep(data.arguments, 0);
      } else if ('arguments' in data) {
        // Fails CLOSED on a shape this function never learned to walk —
        // same principle as `maskedLoggerPrinter`
        // (`apps/edge-api/src/utils/logger-printer.ts`): an unrecognized
        // shape is never passed through raw. The installed SDK always
        // builds this as an array (confirmed by driving the real
        // integration in `sentry-console-breadcrumb-architecture.test.ts`);
        // a future SDK version or a different console-instrumenting
        // integration producing something else must not ride out unmasked
        // just because it isn't the one shape this branch knows how to
        // walk.
        delete data.arguments;
      }
    } else if (
      breadcrumb.category === 'ui.click' ||
      breadcrumb.category === 'ui.keypress'
    ) {
      // The installed SDK (`_getDomBreadcrumbHandler`,
      // `@sentry/browser/.../integrations/breadcrumbs.js:66-99`) never puts
      // the selector under `data.target` in this version — only `message`
      // (handled above) carries it, and `data` (when present at all) holds
      // just `ui.component_name`. Handled here anyway, defensively: this is
      // the shape an earlier hand-built test in this suite already assumed,
      // and a field name a different/future SDK version could plausibly use
      // for the same selector string — costs nothing when absent.
      if (typeof data.target === 'string')
        data.target = stripSelectorAttributeValues(data.target);
    }
  }
}

/**
 * `ReplayEvent extends Event` with `urls: string[]`
 * (`@sentry/core/build/types/types/replay.d.ts:6-15`) — marked `@hidden`/
 * Beta and not re-exported through `@sentry/react`'s public surface, so the
 * one field this function needs is declared locally instead of importing an
 * unstable type.
 */
interface ReplayEnvelopeEventShape {
  type?: string;
  urls?: string[];
}

/**
 * Sanitize a Sentry browser event before it leaves the process. Wired as a
 * global event processor in `sentry.ts` (see this module's docblock for
 * why that reaches error, transaction, AND replay envelope events with one
 * registration).
 */
export function maskSentryBrowserEvent<E extends Event>(event: E): E {
  if (event.request?.url) {
    event.request.url = maskUrlLike(event.request.url);
  }
  if (event.request?.headers) {
    // Only the address-bearing header — see this module's docblock (I4).
    // `User-Agent` stays; deleting the whole object silently disabled
    // browser/OS triage for every transaction event. Case-insensitive key
    // match (review round 2, Minor 11) rather than a `Referer`/`referer`
    // spelling list: this SDK always writes exactly `Referer`
    // (`@sentry/browser/.../helpers.js:88-101`), so a `REFERER` never
    // occurs today — but a spelling list is exactly the denylist-of-known-
    // forms pattern this file exists to avoid, and a case-insensitive scan
    // costs nothing extra.
    for (const key of Object.keys(event.request.headers)) {
      if (key.toLowerCase() === 'referer') {
        delete event.request.headers[key];
      }
    }
    if (Object.keys(event.request.headers).length === 0) {
      delete event.request.headers;
    }
  }
  if (event.request && 'query_string' in event.request) {
    delete event.request.query_string;
  }

  if (event.transaction) {
    event.transaction = maskMethodAndPath(event.transaction);
  }

  const traceData = event.contexts?.trace?.data;
  if (traceData) {
    maskSpanAttributeData(traceData as Record<string, unknown>);
  }

  if (event.spans) {
    for (const span of event.spans) {
      if (span.data) {
        maskSpanAttributeData(span.data as Record<string, unknown>);
      }
      if (typeof span.description === 'string') {
        span.description = maskMethodAndPath(span.description);
      }
    }
  }

  maskBreadcrumbs(event);

  // Replay's envelope event (`type: 'replay_event'`) carries its own
  // ordered list of every URL visited during the segment — a field no
  // other event type has (see docblock: this is channel C1).
  const replayEvent = event as unknown as ReplayEnvelopeEventShape;
  if (replayEvent.type === 'replay_event' && Array.isArray(replayEvent.urls)) {
    replayEvent.urls = replayEvent.urls.map(maskUrlLike);
  }

  return event;
}

const MAX_RECORDING_EVENT_MASK_DEPTH = 6;

/**
 * Deep-walk mask for `beforeAddRecordingEvent` (the Replay recording
 * pipeline's own hook — a DIFFERENT mechanism from
 * `maskSentryBrowserEvent`/`prepareEvent`, see `sentry.ts`). Only
 * `EventType.Custom` recording events reach this hook at all
 * (`@sentry/replay/build/npm/cjs/index.js:5793-5795,5864-5874` —
 * `maybeApplyCallback` gates on `isCustomEvent`), and their payload shape
 * varies by source (`navigation.push`, a performance-entry span, a network
 * request/response span, a console breadcrumb — all built by
 * `createPerformanceSpans`/`createBreadcrumb`,
 * `@sentry/replay/build/npm/cjs/index.js:6183-6201,4888-...`). Rather than
 * enumerate every payload shape (the same denylist-of-known-fields pattern
 * this remediation program calls out — see `docs/plans/
 * 2026-07-31-egress-remediation.md` §1), this walks every string value in
 * the event's `data.payload` and runs it through `maskInvitationTokenInPath`
 * unconditionally: the function only ever rewrites text that matches the
 * known secret's path shape, so it cannot corrupt an unrelated string field
 * (a duration, an HTTP method, a DOM selector) — it simply never matches.
 * Depth-bounded against unexpected cycles/size, not because six levels was
 * measured as sufficient for every payload shape.
 *
 * `maskStringsDeep` below this function is also called from
 * `maskBreadcrumbs`'s `'console'` branch, on `breadcrumb.data.arguments` —
 * the SAME array shape (a console call's raw argument list), reached
 * through the OTHER mechanism (`prepareEvent`, error/transaction/replay
 * envelope events) rather than the Replay recording pipeline. One walker,
 * two independent carriers of the same shape — see `maskBreadcrumbs`.
 */
export function maskReplayRecordingEvent(event: unknown): unknown {
  if (event && typeof event === 'object' && 'data' in event) {
    const data = (event as { data?: unknown }).data;
    if (data && typeof data === 'object' && 'payload' in data) {
      maskStringsDeep((data as { payload?: unknown }).payload, 0);
    }
  }
  return event;
}

function maskStringsDeep(value: unknown, depth: number): void {
  if (
    depth > MAX_RECORDING_EVENT_MASK_DEPTH ||
    value === null ||
    typeof value !== 'object'
  ) {
    return;
  }
  if (value instanceof Error && typeof value.message === 'string') {
    // `Error.prototype.message` (and a subclass's own message, assigned via
    // `super(message)` — e.g. this app's two `ApiError` classes) is a
    // NON-enumerable own property by spec default. The generic
    // `Object.keys()` walk below never sees it, so an Error passed as a
    // console argument (`console.error('failed', err)` —
    // ImportProgressModal.tsx, EmbeddedConsentGate.tsx,
    // the `captureException` fallback in
    // `sentry.ts`, …) would otherwise carry `.message` through completely
    // unmasked, even though the identical text IS masked when the SDK
    // flattens it into `breadcrumb.message` (see `maskBreadcrumbs`) — an
    // asymmetry between two fields of the SAME breadcrumb. Handled
    // explicitly, then falls through to the generic walk below so an
    // `ApiError`'s own enumerable fields (`status`, `code`, `details`/
    // `data`, …) still get walked normally.
    //
    // Review round 1, Minor: a FROZEN Error throws on this assignment
    // (`TypeError: Cannot assign to read only property 'message'`) —
    // unreachable in this app today (no call site freezes an Error before
    // logging it), but an event processor that throws costs the WHOLE
    // event, not just this one field (`prepareEvent`'s
    // `notifyEventProcessors` has no partial-failure mode). Caught and left
    // as is rather than letting that propagate — the rest of this
    // breadcrumb, and every other breadcrumb/field on the event, still gets
    // masked.
    try {
      value.message = maskInvitationTokenInPath(value.message);
    } catch {
      // Frozen/sealed — nothing to do, see above.
    }
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const item: unknown = value[i];
      if (typeof item === 'string') {
        value[i] = maskInvitationTokenInPath(item);
      } else {
        maskStringsDeep(item, depth + 1);
      }
    }
    return;
  }
  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const propValue = obj[key];
    if (typeof propValue === 'string') {
      obj[key] = maskInvitationTokenInPath(propValue);
    } else if (propValue && typeof propValue === 'object') {
      maskStringsDeep(propValue, depth + 1);
    }
  }
}
